import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Star, Trash2, Save, Edit3, X, Sparkles } from "lucide-react";
import { api } from "../lib/api";

/**
 * Segretaria IA · Memoria (permanente per l'utente).
 * CRUD + pin/unpin.  Route: /settings/memoria-segretaria
 */
export default function MemoriaSegretaria() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ content: "", category: "generale", pinned: false });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await api.memoriesList()); }
    catch { toast.error("Impossibile caricare le memorie"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setCreating(true); setEditingId(null);
    setDraft({ content: "", category: "generale", pinned: false });
  };
  const startEdit = (m) => {
    setEditingId(m.id); setCreating(false);
    setDraft({ content: m.content, category: m.category, pinned: m.pinned });
  };
  const cancel = () => { setEditingId(null); setCreating(false); setDraft({ content: "", category: "generale", pinned: false }); };

  const save = async () => {
    const c = draft.content.trim();
    if (c.length < 2) { toast.error("Testo troppo breve"); return; }
    try {
      if (editingId) {
        await api.memoriesUpdate(editingId, { content: c, category: draft.category, pinned: draft.pinned });
        toast.success("Memoria aggiornata");
      } else {
        await api.memoriesCreate({ content: c, category: draft.category, pinned: draft.pinned });
        toast.success("Memoria aggiunta");
      }
      cancel();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const togglePin = async (m) => {
    try { await api.memoriesUpdate(m.id, { pinned: !m.pinned }); await load(); }
    catch { toast.error("Impossibile fissare"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Eliminare questa memoria?\n\n"${m.content.slice(0, 100)}"`)) return;
    try {
      await api.memoriesDelete(m.id);
      toast.success("Memoria eliminata");
      await load();
    } catch { toast.error("Impossibile eliminare"); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="page-memoria-segretaria">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Sparkles size={22} style={{ color: "var(--pxos-accent)" }}/>
            Memoria Segretaria IA
          </h1>
          <p className="text-sm opacity-70 mt-1">
            Note permanenti che la Segretaria consulta ad ogni conversazione (preferenze, decisioni, contatti, ricorrenze).
            Fissa quelle più importanti con la stellina.
          </p>
        </div>
        <button onClick={startNew} className="pxos-btn-primary text-sm" data-testid="memoria-new-btn">
          <Plus size={14}/> Nuova
        </button>
      </header>

      {(creating || editingId) && (
        <div className="pxos-card p-4 space-y-3" data-testid="memoria-editor">
          <div className="flex items-center gap-2">
            <input
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              placeholder="Categoria (es. preferenze, contatti)"
              maxLength={60}
              className="pxos-input flex-1 text-sm"
              data-testid="memoria-category-input"
            />
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={draft.pinned}
                     onChange={(e) => setDraft({ ...draft, pinned: e.target.checked })}
                     data-testid="memoria-pinned-toggle"/>
              <Star size={14}/> Fissa
            </label>
          </div>
          <textarea
            value={draft.content}
            onChange={(e) => setDraft({ ...draft, content: e.target.value })}
            placeholder="Contenuto della memoria… (max 2000 caratteri)"
            maxLength={2000}
            rows={4}
            className="pxos-input w-full text-sm"
            data-testid="memoria-content-input"
          />
          <div className="flex items-center gap-2">
            <button onClick={save} className="pxos-btn-primary text-sm" data-testid="memoria-save-btn">
              <Save size={14}/> {editingId ? "Aggiorna" : "Salva"}
            </button>
            <button onClick={cancel} className="text-sm underline opacity-70" data-testid="memoria-cancel-btn">
              <X size={14}/> Annulla
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="opacity-70 text-sm" data-testid="memoria-loading">Caricamento…</div>
      ) : items.length === 0 && !creating ? (
        <div className="pxos-card p-6 text-center opacity-70 text-sm" data-testid="memoria-empty">
          Nessuna memoria salvata. Crea la prima con il bottone in alto,
          oppure dì alla Segretaria "ricorda che…".
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => (
            <li key={m.id} className="pxos-card p-3 flex items-start gap-2" data-testid={`memoria-item-${m.id}`}>
              <button onClick={() => togglePin(m)}
                      className="mt-0.5"
                      aria-label={m.pinned ? "Rimuovi da fissate" : "Fissa in cima"}
                      data-testid={`memoria-pin-${m.id}`}>
                <Star size={16} fill={m.pinned ? "#F59E0B" : "none"}
                      color={m.pinned ? "#F59E0B" : "currentColor"}
                      className={m.pinned ? "" : "opacity-40 hover:opacity-100"}/>
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest opacity-60">{m.category}</div>
                <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>
                <div className="text-[10px] opacity-50 mt-1">
                  Aggiornata: {new Date(m.updated_at).toLocaleString("it-IT")}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => startEdit(m)} className="p-1.5 hover:bg-black/5 rounded"
                        aria-label="Modifica" data-testid={`memoria-edit-${m.id}`}>
                  <Edit3 size={14}/>
                </button>
                <button onClick={() => remove(m)} className="p-1.5 hover:bg-black/5 rounded"
                        aria-label="Elimina" data-testid={`memoria-delete-${m.id}`}>
                  <Trash2 size={14} style={{ color: "#B42318" }}/>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
